import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Netzzuordnung } from '../../domain/model/netzzuordnung';
import { map } from 'rxjs/operators';
import { NetzzuordnungFilter } from './netzzuordnung-filter';
import { Pageable } from '../../common/paging/pageable';
import { NetzzuordnungSnapshot } from '../../domain/model/netzzuordnung-snapshot';

// TODO(rb) move
export class Page<T> {
    content: T[];
    first: boolean;
    last: boolean;
    number: number;
    numberOfElements: number;
    size: number;
    // sort: {sorted: true, unsorted: false}
    totalElements: number;
    totalPages: number;
}

@Injectable({
    providedIn: 'root',
})
export class NetzzuordnungService {
    private http = inject(HttpClient);

    getOrtsnamen(stadtwerkeId?: string, filter?: string): Observable<string[]> {
        let params = new HttpParams();

        if (stadtwerkeId) {
            params = params.set('stadtwerkeId', stadtwerkeId);
        }

        if (filter) {
            params = params.set('filter', filter);
        }

        return this.http.get(environment.API_URL + '/netzzuordnung/ort', { params }) as Observable<string[]>;
    }

    getStationsnamen(stadtwerkeId?: string, ortsname?: string, filter?: string): Observable<string[]> {
        let params = new HttpParams();

        if (stadtwerkeId) {
            params = params.set('stadtwerkeId', stadtwerkeId);
        }

        if (ortsname) {
            params = params.set('ortsname', ortsname);
        }
        if (filter) {
            params = params.set('filter', filter);
        }

        return this.http.get(environment.API_URL + '/netzzuordnung/station', { params }) as Observable<string[]>;
    }

    getNetzzuordnungId(stadtwerkeId?: string, filter?: string): Observable<string[]> {
        let params = new HttpParams();

        if (stadtwerkeId) {
            params = params.set('stadtwerkeId', stadtwerkeId);
        }

        if (filter) {
            params = params.set('filter', filter);
        }

        return this.http.get(environment.API_URL + '/netzzuordnung/netzzuordnungId', { params }) as Observable<string[]>;
    }

    getTrafoFeldNamen(stadtwerkeId: string, ortsname?: string, stationsname?: string, filter?: string): Observable<string[]> {
        let params = new HttpParams();

        if (stadtwerkeId) {
            params = params.set('stadtwerkeId', stadtwerkeId);
        }

        if (ortsname) {
            params = params.set('ortsname', ortsname);
        }

        if (stationsname) {
            params = params.set('stationsname', stationsname);
        }

        if (filter) {
            params = params.set('filter', filter);
        }

        return this.http.get(environment.API_URL + '/netzzuordnung/trafoFeldName', { params }) as Observable<string[]>;
    }

    getUuid(stadtwerkeId?: string, filter?: string): Observable<string[]> {
        let params = new HttpParams();

        if (stadtwerkeId) {
            params = params.set('stadtwerkeId', stadtwerkeId);
        }

        if (filter) {
            params = params.set('filter', filter);
        }

        return this.http.get(environment.API_URL + '/netzzuordnung/uuid', { params }) as Observable<string[]>;
    }

    getNetzgruppe(stadtwerkeId?: string, filter?: string): Observable<string[]> {
        let params = new HttpParams();

        if (stadtwerkeId) {
            params = params.set('stadtwerkeId', stadtwerkeId);
        }

        if (filter) {
            params = params.set('filter', filter);
        }

        return this.http.get(environment.API_URL + '/netzzuordnung/netzgruppe', { params }) as Observable<string[]>;
    }

    getNetzzuordnungen(stadtwerkeId: string, ortsname: string, stationsname?: string, trafoFeldName?: string): Observable<Netzzuordnung[]> {
        const params = new HttpParams()
            .set('stadtwerkeId', stadtwerkeId)
            .set('ortsname', ortsname)
            .set('stationsname', stationsname)
            .set('trafoFeldName', trafoFeldName);

        return this.http.get(environment.API_URL + '/netzzuordnung', { params }) as Observable<Netzzuordnung[]>;
    }

    public getAllNetzzuordnungen(
        pageable?: Pageable,
        filter?: NetzzuordnungFilter,
    ): Observable<Page<Netzzuordnung>> {
        const params = this.toHttpParams(pageable, filter);
        return this.http.get(environment.API_URL + '/netzzuordnung/list', { params }) as Observable<Page<Netzzuordnung>>;
    }


    getFirstNetzzuordnung(stadtwerkeId: string, ortsname: string, stationsname: string, trafoFeldName: string): Observable<Netzzuordnung> {
        return this.getNetzzuordnungen(stadtwerkeId, ortsname, stationsname, trafoFeldName)
            .pipe(map((netzzuordnungen: Netzzuordnung[]) => netzzuordnungen[0]));
    }


    private toHttpParams(pageable?: Pageable,
        filter?: NetzzuordnungFilter): HttpParams {

        let params = new HttpParams({
            fromObject: pageable || {} as any,
        });

        if (filter) {
            if (filter.ortsname) {
                params = params.set('ortsname', filter.ortsname);
            }
            if (filter.stationsname) {
                params = params.set('stationsname', filter.stationsname);
            }
            if (filter.trafoFeldName) {
                params = params.set('trafoFeldName', filter.trafoFeldName);
            }
            if (filter.uuid) {
                params = params.set('uuid', filter.uuid);
            }
            if (filter.technischerPlatz) {
                params = params.set('technischerPlatz', filter.technischerPlatz);
            }
            if (filter.stadtwerkeId) {
                params = params.set('stadtwerkeId', filter.stadtwerkeId);
            }
            if (filter.netzbetreiberName) {
                params = params.set('netzbetreiberName', filter.netzbetreiberName);
            }
            if (filter.netzbetreiberTeilgebiet) {
                params = params.set('netzbetreiberTeilgebiet', filter.netzbetreiberTeilgebiet);
            }
            if (filter.uuidDateStart) {
                params = params.set('uuidDateStart', filter.uuidDateStart.toISOString(true));
            }
            if (filter.uuidDateEnd) {
                params = params.set('uuidDateEnd', filter.uuidDateEnd.toISOString(true));
            }
            if (filter.netzgruppe) {
                params = params.set('netzgruppe', filter.netzgruppe);
            }
        }

        return params;
    }

    public getSnapshot(stadtwerkeId?: string): Observable<NetzzuordnungSnapshot> {
        let params = new HttpParams();

        if (stadtwerkeId) {
            params = params.set('stadtwerkeId', stadtwerkeId);
        }

        return this.http.get<NetzzuordnungSnapshot>(environment.API_URL + '/netzzuordnung/snapshot', { params });
    }
}
